<template>
  <page-header title="Ticket alta de cargas masiva" :breadcrumRoutes="routes">
    <template #tags>
      <tags color="processing">ID: {{ ticketId }}</tags>
    </template>

    <template #buttons v-if="showCancelAndExecute">
      <a-button :loading="loading" :disabled="loading" @click="handlePreCancel">
        Cancelar
      </a-button>
      <a-button
        :loading="loading"
        :disabled="loading"
        type="primary"
        class="ml-2"
        @click="handlePreSuccess"
      >
        Ejecutar
      </a-button>
    </template>
  </page-header>

  <massive-tickets-list
    title="Listado de altas de cargas"
    defaultRedirect="AltaCargasMasivaDetalle"
    @table-change="onTableChange"
    @set-tickets-data="setIncorporationsData"
  />

  <a-card class="mt-4">
    <a-row :gutter="[16, 8]">
      <a-col :xs="24" :md="12">
        <ticket-data
          :data="tableData"
          title="Datos ticket"
          :loadTicket="loadTicket"
        />
        <insurance-message class="mt-3" :message="insuranceMessage" />
      </a-col>

      <a-col :xs="24" :md="12">
        <attached-documents
          title="Archivo adjunto"
          :data="attachedFiles"
          :loading="loadingDocument"
        />

        <processed-file
          class="mt-3"
          title="Archivo con observaciones"
          :ticketGroupId="ticketId"
          :loading="loadingMovementFiles"
        />

        <attached-documents
          class="mt-3"
          title="Archivos opcionales"
          :data="attachedOptionalFiles"
          :loading="loadingOptionalFiles"
        />

        <comments
          :comments="comments"
          ticketType="masive"
          :idToTicket="ticketId"
          @new_comment="getNewCommment"
          class="mt-3"
        />
      </a-col>
    </a-row>
  </a-card>

  <a-modal
    :wrapClassName="'modal-xs'"
    v-model:visible="visible"
    :title="modalStyle.title"
    @ok="handleOk"
    centered
    :maskClosable="false"
  >
    <a-typography-title class="text-gray-4 fw-regular mb-4" :level="4">
      {{ modalStyle.text }}
    </a-typography-title>
    <a-row :gutter="16" align="middle">
      <a-col :span="3" class="mr-2">
        <info-circle-outlined
          v-if="modalStyle.contentText !== 'Sin casos pendientes'"
          :style="{ fontSize: '21px', color: '#FAAD14' }"
        />
        <check-circle-outlined
          v-if="modalStyle.contentText === 'Sin casos pendientes'"
          :style="{ fontSize: '21px', color: '#00DC92' }"
        />
      </a-col>
      <a-col :span="20">
        <a-typography-title class="text-gray-4 fw-regular ml-2" :level="5">
          {{ modalStyle.contentText }}
        </a-typography-title>
      </a-col>
    </a-row>
    <template #footer>
      <a-row type="flex" justify="end">
        <a-col>
          <a-space>
            <a-button @click="handleOk">Volver atrás</a-button>
            <a-button
              v-if="modalStyle.mode === 'cancelTicket'"
              key="submit"
              type="danger"
              :loading="loading"
              @click="handleCancel"
            >
              Sí, cancelar
            </a-button>
            <a-button
              v-if="modalStyle.mode === 'successTicket'"
              key="submit"
              type="primary"
              :loading="loading"
              @click="handleSuccess"
            >
              Confirmar
            </a-button>
          </a-space>
        </a-col>
      </a-row>
    </template>
  </a-modal>
</template>

<script>
import { reactive, ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { InfoCircleOutlined, CheckCircleOutlined } from '@ant-design/icons-vue';
import moment from 'moment';
// Store
import {
  // GET_AM_DETAIL,
  // FETCH_AM_DETAIL,
  TICKET_GROUPS_FETCH_TICKET_GROUP,
  TICKET_GROUPS_GET_TICKET_GROUP,
  GET_AM_DETAIL_FILE,
  FETCH_AM_DETAIL_FILE,
  TICKET_GROUPS_FETCH_TICKETS,
  PRECANCEL_MASSIVE_INCORPORATION,
  PRESUCCESS_MASSIVE_DEPENDANTS_INCORPORATION,
  SUCCESS_MASSIVE_DEPENDANTS_INCORPORATION,
  CANCEL_MASSIVE_INCORPORATION,
  FETCH_MASSIVE_COMMENTS,
  GET_LOGGED_USER,
  FETCH_USER_BY_ID,
  FETCH_OPTIONAL_FILES_DEPENDENTS_TICKET_GROUP,
  GET_OPTIONAL_FILES_DEPENDENTS_TICKET_GROUP,
  TICKET_GROUPS_FETCH_MOVEMENT_FILES
} from '@/store/types';
// Components
import Tags from '@/components/tags.vue';
import PageHeader from '@/components/page-header.vue';
import TicketData from '@/components/tickets/ticket-data.vue';
import InsuranceMessage from '@/components/tickets/insurer-message.vue';
import ProcessedFile from '@/components/tickets/processed-file.vue';
import AttachedDocuments from '@/components/tickets/attached-documents.vue';
import MassiveTicketsList from '@/components/tickets/massive-tickets-list.vue';
import Comments from '@/components/tickets/comments.vue';
// Utils
import openNotificationWithIcon from '@/utils/notifications';
import parseDate from '@/utils/dateFormatter';
import rutHelper from '@/utils/Ruts';

export default {
  name: 'AltaCargasMasivaDetalle',
  components: {
    'ticket-data': TicketData,
    'insurance-message': InsuranceMessage,
    'attached-documents': AttachedDocuments,
    'processed-file': ProcessedFile,
    'massive-tickets-list': MassiveTicketsList,
    'info-circle-outlined': InfoCircleOutlined,
    'check-circle-outlined': CheckCircleOutlined,
    'page-header': PageHeader,
    tags: Tags,
    comments: Comments
  },
  setup() {
    const route = useRoute();
    const store = useStore();
    const ticketId = ref(route.params.id);
    const userRole = ref(store.getters[GET_LOGGED_USER].roleName);

    // * Rutas
    const routes = ref([
      {
        path: '/altas-cargas',
        breadcrumbName: 'Altas cargas'
      },
      {
        path: '/altas-cargas#massive',
        breadcrumbName: 'Masiva'
      },
      {
        breadcrumbName: `#${ticketId.value}`
      }
    ]);

    //* Get Data
    const ticketData = ref({});
    const loadTicket = ref(false);
    const loadingMassiveTicket = ref(false);
    const router = useRouter();
    const loadingDocument = ref(false);

    const getTicketData = async () => {
      loadTicket.value = true;
      loadingMassiveTicket.value = true;
      await store
        // .dispatch(FETCH_AM_DETAIL, ticketId.value)
        .dispatch(TICKET_GROUPS_FETCH_TICKET_GROUP, ticketId.value)
        .catch(() => router.push({ name: 'Missing' }));
      // ticketData.value = await store.getters[GET_AM_DETAIL];
      ticketData.value = await store.getters[TICKET_GROUPS_GET_TICKET_GROUP];

      loadTicket.value = false;
      loadingMassiveTicket.value = false;
    };
    getTicketData().then(() => {
      fillTicketGroupData();
      getBroker();
    });

    const filters = ref({});

    const getTicketDataWithPagination = async (pagination) => {
      loadTicket.value = true;
      loadingMassiveTicket.value = true;

      const page = pagination?.current ?? 1;
      const size = pagination?.pageSize ?? 10;

      await store
        .dispatch(TICKET_GROUPS_FETCH_TICKETS, {
          ticketGroupId: ticketId.value,
          filters: filters.value,
          page,
          size
        })
        .catch(() => router.push({ name: 'Missing' }));

      loadTicket.value = false;
      loadingMassiveTicket.value = false;
    };

    const onTableChange = ({ pagination, filters: newFilters }) => {
      filters.value = { ...newFilters };

      const safePagination = {
        current: pagination?.current ?? 1,
        pageSize: pagination?.pageSize ?? 10
      };

      getTicketDataWithPagination(safePagination);
    };

    const attachedFiles = ref([]);
    const attachedOptionalFiles = ref([]);
    const loadingOptionalFiles = ref(false);

    const getAttachedOptionalFiles = async () => {
      loadingOptionalFiles.value = true;
      try {
        if (userRole.value != 'rrhh') {
          await store.dispatch(
            FETCH_OPTIONAL_FILES_DEPENDENTS_TICKET_GROUP,
            ticketId.value
          );
        }
        attachedOptionalFiles.value =
          await store.getters[GET_OPTIONAL_FILES_DEPENDENTS_TICKET_GROUP];
        loadingOptionalFiles.value = false;
      } catch (error) {
        openNotificationWithIcon(
          'error',
          'Error',
          `Error al cargar archivos opcionales del Ticket. ${error}`
        );
        loadingOptionalFiles.value = false;
      }
    };
    const getAttachedFiles = async () => {
      loadingDocument.value = true;
      try {
        await store
          .dispatch(FETCH_AM_DETAIL_FILE, ticketId.value)
          .catch((err) =>
            openNotificationWithIcon(
              'error',
              'Error',
              `Error al cargar archivo del Ticket. ${err.response.data.message}`
            )
          );
        attachedFiles.value = await store.getters[GET_AM_DETAIL_FILE];
        loadingDocument.value = false;
      } catch (error) {
        console.log('error', error);
        loadingDocument.value = false;
      }
    };
    getAttachedFiles();
    getAttachedOptionalFiles();

    const tableData = reactive([
      { key: 'Fecha de creación', value: '' },
      { key: 'Broker', value: '' },
      { key: 'Aseguradora', value: '' },
      { key: 'Holding', value: '' },
      { key: 'Subholding', value: '' },
      { key: 'Empresa', value: '' },
      { key: 'RUT Empresa', value: '' },
      { key: 'Póliza', value: '' },
      { key: 'Grupo', value: '' }
    ]);
    const insuranceMessage = ref('');

    const fillTicketGroupData = () => {
      tableData[0].value = ticketData.value.object?.createdAt
        ? moment(ticketData.value.object?.createdAt).format('DD/MM/YYYY')
        : '';
      tableData[2].value = ticketData.value.object?.insuranceCompany || '';
      tableData[3].value = ticketData.value.object?.holding || '';
      tableData[4].value = ticketData.value.object?.subHolding || '';
      tableData[5].value = ticketData.value.object?.company || '';
      tableData[6].value = ticketData.value.object?.rutCompany
        ? rutHelper.formatRut(ticketData.value.object.rutCompany)
        : '';

      tableData[7].value = ticketData.value.object?.policies.join(', ') || '';
      tableData[8].value = ticketData.value.object?.groups.join(', ') || '';

      insuranceMessage.value = ticketData.value?.message || '';
    };
    const getBroker = async () => {
      const response = await store.dispatch(
        FETCH_USER_BY_ID,
        ticketData.value.object.brokerId
      );
      tableData[1].value = response.data.body.name;
    };

    const showCancelAndExecute = computed(() =>
      incorporationsData.value.some((d) => {
        if (payloadObject.value !== null) {
          if (d.ticketState == 3 || d.ticketState == 4) {
            return false;
          }
          loading.value = false;
          return true;
        }
      })
    );

    const incorporationsData = ref([]);
    const setIncorporationsData = (ticketsRows) => {
      incorporationsData.value = ticketsRows;
    };

    const payloadObject = ref({});
    const loading = ref(false);
    const visible = ref(false);
    const modalStyle = reactive({
      title: '',
      text: '',
      contentText: '',
      mode: ''
    });

    const showModal = () => {
      visible.value = true;
      loading.value = false;
    };

    const handleOk = () => {
      visible.value = false;
      loading.value = false;
    };

    const handlePreCancel = async () => {
      let cantTickets = null;
      let casesCount = 0;
      await store
        .dispatch(PRECANCEL_MASSIVE_INCORPORATION, ticketId.value)
        .then((res) => {
          cantTickets = res.ticketCancels.length;
          if (cantTickets === 0) {
            openNotificationWithIcon(
              'error',
              'Error',
              'No se ha podido cargar la información para cancelar el ticket. No existe ningún ticket para cancelar'
            );
          } else {
            res.ticketCancels.map((a) => {
              if (a.case.length > 0) {
                casesCount++;
              }
            });
            modalStyle.mode = 'cancelTicket';
            modalStyle.title = 'Cancelar ticket de alta masiva';
            modalStyle.text = `¿Estás seguro de que deseas cancelar ${cantTickets} tickets de alta masiva?`;
            if (cantTickets === 1) {
              modalStyle.text = `¿Estás seguro de que deseas cancelar ${cantTickets} ticket de alta masiva?`;
            }
            modalStyle.contentText =
              casesCount > 0
                ? 'Tickets no ejecutados y casos pendientes asociados pasarán a cancelados'
                : 'Sin casos pendientes';
            payloadObject.value = { objectReturn: res };
            showModal();
          }
        })
        .catch((err) =>
          openNotificationWithIcon(
            'error',
            'Error',
            `No se ha podido cargar la información para cancelar el ticket. ${err.response.data.message}.`
          )
        );
    };

    const handleCancel = async () => {
      loading.value = true;
      try {
        await store.dispatch(CANCEL_MASSIVE_INCORPORATION, {
          idAlta: ticketId.value,
          payload: payloadObject.value
        });
        await store
          .dispatch(TICKET_GROUPS_FETCH_TICKETS, {
            ticketGroupId: ticketId.value
          })
          .then(() => {
            openNotificationWithIcon(
              'success',
              'Correcto',
              `Ticket ${ticketId.value} cancelado exitosamente`
            );
            getTicketData().then(() => {
              fillTicketGroupData();
              getBroker();
            });
            loading.value = false;
            visible.value = false;
          });
      } catch (error) {
        loading.value = false;
        openNotificationWithIcon(
          'error',
          'Error',
          `No se ha podido cancelar el ticket ${ticketId.value}. ${error.response.data.message}`
        );
      }
    };

    const handlePreSuccess = async () => {
      let cantTickets = null;
      let pendingCount = 0;
      await store
        .dispatch(PRESUCCESS_MASSIVE_DEPENDANTS_INCORPORATION, ticketId.value)
        .then((res) => {
          cantTickets = res.readyTickets.length;
          if (cantTickets === 0) {
            openNotificationWithIcon(
              'error',
              'Error',
              'No se ha podido cargar la información para ejecutar el ticket. No existe ningún ticket para ejecutar.'
            );
          } else {
            res.pendingTickets.map(() => {
              pendingCount++;
            });
            modalStyle.mode = 'successTicket';
            modalStyle.title = 'Ejecutar ticket de alta de cargas masiva';
            modalStyle.text = `¿Estás seguro de que deseas ejecutar ${cantTickets} tickets de alta de cargas masiva?`;
            if (cantTickets === 1) {
              modalStyle.text = `¿Estás seguro de que deseas ejecutar ${cantTickets} ticket de alta de cargas masiva?`;
            }
            modalStyle.contentText =
              pendingCount > 0
                ? `No se están considerando ${pendingCount} tickets pendientes con casos asociados.`
                : 'Sin casos pendientes';
            if (pendingCount === 1) {
              modalStyle.contentText = `No se está considerando ${pendingCount} ticket pendiente con casos asociados.`;
            }
            payloadObject.value = res;
            showModal();
          }
        })
        .catch((err) =>
          openNotificationWithIcon(
            'error',
            'Error',
            `No se ha podido cargar la información para ejecutar el ticket. ${err.response.data.message}.`
          )
        );
    };

    const handleSuccess = async () => {
      loading.value = true;
      try {
        await store.dispatch(SUCCESS_MASSIVE_DEPENDANTS_INCORPORATION, {
          idAlta: ticketId.value,
          payload: payloadObject.value
        });
        await store
          .dispatch(TICKET_GROUPS_FETCH_TICKETS, {
            ticketGroupId: ticketId.value
          })
          .then(() => {
            console.log('Entró al then');
            openNotificationWithIcon(
              'success',
              'Correcto',
              `Ticket ${ticketId.value} ejecutado exitosamente`
            );
            getTicketData().then(() => {
              fillTicketGroupData();
              getBroker();
            });
            visible.value = false;
          });
      } catch (error) {
        console.log('Entró al catch');
        openNotificationWithIcon(
          'error',
          'Error',
          `No se ha podido ejecutar el ticket ${ticketId.value}. ${error.response.data.message}`
        );
      } finally {
        loading.value = false;
      }
    };

    // * Comentarios

    const comments = ref([]);
    const getComments = async () => {
      comments.value = [];
      const resp = await store.dispatch(FETCH_MASSIVE_COMMENTS, ticketId.value);
      for (let i = 0; i < resp.length; i++) {
        const element = resp[i];
        const broker_name = await getBrokerName(element.brokerId);
        comments.value.push({
          description: parseDate(element.createdAt),
          title: element.message,
          author: broker_name
        });
      }
    };

    const getBrokerName = async (id) => {
      const response = await store.dispatch(FETCH_USER_BY_ID, id);
      return response.data.body.name;
    };

    getComments();

    const getNewCommment = async (value) => {
      const { description } = value;
      const { brokerId } = value;
      const brokerName = await getBrokerName(brokerId);

      comments.value.push({
        title: value.title,
        description: parseDate(description),
        author: brokerName
      });
    };

    const loadingMovementFiles = ref(false);

    const getMovementFiles = async (ticketGroupId) => {
      loadingMovementFiles.value = true;
      try {
        await store.dispatch(TICKET_GROUPS_FETCH_MOVEMENT_FILES, ticketGroupId);
      } catch (e) {
        console.error('Error:', e);
      } finally {
        loadingMovementFiles.value = false;
      }
    };

    onMounted(async () => {
      await store.dispatch(TICKET_GROUPS_FETCH_TICKETS, {
        ticketGroupId: ticketId.value,
        filters: filters.value,
        page: 1,
        size: 10
      });
      getMovementFiles(ticketId.value);
    });

    return {
      // Variables
      loading,
      visible,
      modalStyle,
      incorporationsData,
      routes,
      ticketId,
      tableData,
      insuranceMessage,
      attachedFiles,
      ticketData,
      comments,
      userRole,
      // Methods
      showCancelAndExecute,
      handlePreCancel,
      handleCancel,
      handlePreSuccess,
      handleSuccess,
      handleOk,
      getNewCommment,
      loadTicket,
      loadingMassiveTicket,
      loadingDocument,
      loadingOptionalFiles,
      getAttachedOptionalFiles,
      attachedOptionalFiles,
      loadingMovementFiles,
      onTableChange,
      setIncorporationsData
    };
  }
};
</script>
